import { z } from 'zod';

export const userRoles = ['admin', 'user'] as const;
export type UserRole = (typeof userRoles)[number];

export const userRoleOptions: { value: UserRole; label: string }[] = [
	{ value: 'user', label: 'User' },
	{ value: 'admin', label: 'Admin' },
];

export const UserRecordSchema = z.object({
	id: z.string().uuid(),
	role: z.enum(userRoles).default('user'),
	name: z.string().max(127),
	firstName: z
		.string()
		.max(63, { message: 'First name must be at most 63 characters long' })
		.min(2, { message: 'First name must be at least 2 characters long' }),
	lastName: z
		.string()
		.max(63, { message: 'Last name must be at most 63 characters long' })
		.min(2, { message: 'Last name must be at least 2 characters long' }),
	email: z.string().max(127).email(),
	emailVerified: z.date().nullable(),
	password: z
		.string()
		.min(8, { message: 'Password must be at least 8 characters long' })
		.max(127, { message: 'Password must be at most 127 characters' })
		.regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter' })
		.regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter' })
		.regex(/\d/, { message: 'Password must contain at least 1 digit' })
		.regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'Password must contain at least 1 special character (e.g. @$%^)' })
		.nullable(),
	image: z.string().nullable().optional(),
	_created_at: z.date(),
});
export type UserRecord = z.infer<typeof UserRecordSchema>;

export const NewUserRecordSchema = z.object({
	firstName: z
		.string()
		.max(63, { message: 'First name must be at most 63 characters long' })
		.min(2, { message: 'First name must be at least 2 characters long' }),
	lastName: z
		.string()
		.max(63, { message: 'Last name must be at most 63 characters long' })
		.min(2, { message: 'Last name must be at least 2 characters long' }),
	role: z.enum(userRoles),
	email: z.string().max(127).email(),
	password: z
		.string()
		.min(8, { message: 'Password must be at least 8 characters long' })
		.max(127, { message: 'Password must be at most 127 characters' })
		.regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter' })
		.regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter' })
		.regex(/\d/, { message: 'Password must contain at least 1 digit' })
		.regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'Password must contain at least 1 special character (e.g. @$%^)' }),
});
export type NewUserRecord = z.infer<typeof NewUserRecordSchema>;

export const UpdateUserRecordSchema = UserRecordSchema.omit({ _created_at: true }).partial();
export type UpdateUserRecord = z.infer<typeof UpdateUserRecordSchema>;

export const LoginUserRecordSchema = UserRecordSchema.pick({ email: true, password: true });
export type LoginUserRecord = z.infer<typeof LoginUserRecordSchema>;

export const ForgotPasswordRecordSchema = UserRecordSchema.pick({ email: true });
export type ForgotPasswordRecord = z.infer<typeof ForgotPasswordRecordSchema>;

export const ResetPasswordRecordSchema = z
	.object({
		password: z
			.string()
			.min(8, { message: 'Password must be at least 8 characters long' })
			.max(127, { message: 'Password must be at most 127 characters' })
			.regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter' })
			.regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter' })
			.regex(/\d/, { message: 'Password must contain at least 1 digit' })
			.regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'Password must contain at least 1 special character (e.g. @$%^)' }),
		passwordConfirm: z.string().min(8, { message: 'Please confirm your password' }),
	})
	.superRefine(({ password, passwordConfirm }, ctx) => {
		if (password !== passwordConfirm) {
			ctx.addIssue({
				path: ['passwordConfirm'],
				message: "Passwords doesn't match",
				code: z.ZodIssueCode.custom,
			});
		}
	});
export type ResetPasswordRecord = z.infer<typeof ResetPasswordRecordSchema>;

export const UserProfileSchema = z.object({
	id: z.string().uuid(),
	role: z.enum(userRoles).default('user'),
	name: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string().email(),
	emailVerified: z.date().nullable(),
	image: z.string().nullable().optional(),
	token: z.string().nullable().optional(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;
